import { useRef, useState, type ClipboardEvent, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiAiAssist, apiAiChatApply, apiUpdateShow } from '../api';
import type { AiProposedShowFields, AiVenueCandidate } from '../api';
import { reportSaveError } from '../lib/saveStatus';
import {
  buildShowChanges,
  canApplyShowImport,
  formatFieldValue,
  isReplaceWarning,
  needsMoneyConfirmation,
  showFieldEntries,
} from '../lib/aiShowImport';

interface ImportShowAIModalProps {
  /** Band whose AI credits are metered and whose show is written. */
  bandId: string | null;
  /** The show whose fields are filled. */
  showId: string;
  /** Current values of the show — shown next to each proposal so a filled field
   * is never replaced blindly. */
  current: Record<string, unknown>;
  /** Capability-aware gate: false ⇒ no active control in this modal at all. */
  canWrite: boolean;
  onClose: () => void;
  /** Called after a real write happened — reload the show. */
  onImported: (patch: { venueId?: string; fields: Record<string, string | number> }) => void;
}

const IMAGE_MIMES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

function readFileAsBase64(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result ?? '');
      const comma = raw.indexOf(',');
      resolve(comma === -1 ? raw : raw.slice(comma + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * LOT S2 — "Import show details (AI)".
 *
 * The AI only PROPOSES the fields of this show (read-only, metered). NOTHING is
 * written until the user ticks at least one field and presses Import: then ONE
 * `POST /api/ai/chat/apply` (`update_show_fields`) carries exactly the ticked
 * values — the server re-validates and keeps any field it was not given. A
 * field that already holds a value is flagged "will be replaced" before the
 * write; an image is never sent before the visible consent line is accepted;
 * no venue is ever created (an existing candidate can only be attached).
 *
 * Rendered inline (not through a portal): `.modal-overlay` is `position: fixed`.
 */
export default function ImportShowAIModal({
  bandId,
  showId,
  current,
  canWrite,
  onClose,
  onImported,
}: ImportShowAIModalProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  const [pdfName, setPdfName] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [imageMime, setImageMime] = useState<string | null>(null);
  const [imageName, setImageName] = useState<string | null>(null);
  const [imageConsent, setImageConsent] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [proposed, setProposed] = useState<AiProposedShowFields | null>(null);
  const [venueCandidates, setVenueCandidates] = useState<AiVenueCandidate[]>([]);
  const [venueQuery, setVenueQuery] = useState('');
  const [unreadable, setUnreadable] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [venueChoice, setVenueChoice] = useState<string>('');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<{ applied: boolean; count: number } | null>(null);
  const pdfInputRef = useRef<HTMLInputElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);

  const hasInput = text.trim().length > 0 || pdfBase64 !== null || imageBase64 !== null;
  // An image only leaves the browser after the explicit, visible consent line
  // has been accepted. Without it the extract button never fires a request.
  const consentOk = imageBase64 === null || imageConsent;
  const canExtract = canWrite && !extracting && hasInput && consentOk;
  const canApplyNow = canWrite && !applying && canApplyShowImport(checked);

  const entries = showFieldEntries(proposed);
  const safeEntries = entries.filter((e) => !e.isMoney);
  const moneyEntries = entries.filter((e) => e.isMoney);

  const errorText = (err: unknown): string => {
    const status = (err as { status?: number } | null)?.status;
    const message = err instanceof Error ? err.message : '';
    if (status === 402) return t('showImport.outOfCredits');
    if (status === 422) return `${t('showImport.rejected')}: ${message}`;
    if (status === 502) return t('showImport.providerDown');
    return message || t('showImport.failed');
  };

  const clearProposal = () => {
    setProposed(null);
    setVenueCandidates([]);
    setVenueQuery('');
    setUnreadable([]);
    setWarnings([]);
    setChecked(new Set());
    setVenueChoice('');
    setOutcome(null);
  };

  const runExtract = async () => {
    if (!bandId || !canExtract) return;
    setExtracting(true);
    setError(null);
    clearProposal();
    try {
      const res = await apiAiAssist({
        task: 'import_show',
        bandId,
        ...(imageBase64
          ? { imageBase64, imageMime: imageMime || 'image/jpeg' }
          : pdfBase64
            ? { pdfBase64 }
            : { text }),
      });
      setProposed(res.proposedFields ?? {});
      setVenueCandidates(res.venueCandidates ?? []);
      setVenueQuery(res.venueQuery || '');
      setUnreadable((res.unreadable ?? []).filter((u) => !!u));
      setWarnings((res.warnings ?? []).filter((w) => !!w));
      // NOTHING is pre-ticked: an import is never a bulk surprise.
      setChecked(new Set());
      setVenueChoice('');
    } catch (err) {
      setError(errorText(err));
      reportSaveError('show', err);
    } finally {
      setExtracting(false);
    }
  };

  const toggleField = (field: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field); else next.add(field);
      return next;
    });
  };

  const apply = async () => {
    if (!bandId || !canApplyNow || !proposed) return;
    const changes = buildShowChanges(proposed, checked);
    if (Object.keys(changes).length === 0) {
      setError(t('showImport.nothingToApply'));
      return;
    }
    setApplying(true);
    setError(null);
    try {
      // ONE apply for the whole ticked group: the server re-validates every
      // field and keeps anything it was not given (an empty value never wipes).
      await apiAiChatApply({
        bandId,
        action: {
          action: 'update_show_fields',
          showId,
          changes,
          summary: `${t('showImport.actionSummary')}: ${Object.keys(changes).join(', ')}`,
          // A ticked money field REQUIRES the explicit confirmation, otherwise
          // the server silently ignores it (warning only) — never by omission.
          ...(needsMoneyConfirmation(checked) ? { confirmed_money: true } : {}),
        },
      });
      // Attaching an EXISTING venue is a separate gesture: no venue is ever
      // created here, and "do not touch" (the default) sends nothing.
      let venueId: string | undefined;
      if (venueChoice && venueChoice !== 'skip') {
        await apiUpdateShow(showId, { venue_id: venueChoice } as never);
        venueId = venueChoice;
      }
      setOutcome({ applied: true, count: Object.keys(changes).length });
      onImported({ ...(venueId ? { venueId } : {}), fields: changes });
    } catch (err) {
      setError(errorText(err));
      reportSaveError('show', err);
    } finally {
      setApplying(false);
    }
  };

  const stagePdf = async (file: File) => {
    setPdfBase64(await readFileAsBase64(file));
    setPdfName(file.name);
    setText('');
    setImageBase64(null);
    setImageMime(null);
    setImageName(null);
    setImageConsent(false);
  };

  const stageImage = async (file: File | Blob, name: string) => {
    const mime = file.type || 'image/jpeg';
    if (!IMAGE_MIMES.includes(mime)) {
      setError(t('showImport.imageUnsupported'));
      return;
    }
    setImageBase64(await readFileAsBase64(file));
    setImageMime(mime);
    setImageName(name);
    setPdfBase64(null);
    setPdfName(null);
    setText('');
    // A new image needs a fresh consent — the previous one covered another file.
    setImageConsent(false);
  };

  const onFile = async (file: File | null | undefined) => {
    if (!file) return;
    setError(null);
    try {
      if (file.type.startsWith('image/')) {
        await stageImage(file, file.name);
        return;
      }
      if (/\.pdf$/i.test(file.name) || file.type === 'application/pdf') {
        await stagePdf(file);
        return;
      }
      setError(t('showImport.unsupportedFile'));
    } catch (err) {
      setError(errorText(err));
    }
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    void onFile(e.dataTransfer?.files?.[0]);
  };

  /** Ctrl+V of an image (screenshot / copied picture). Text pastes stay in the
   * textarea — only an actual image item is intercepted. */
  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          e.preventDefault();
          void stageImage(file, t('showImport.pastedImage'));
        }
        return;
      }
    }
  };

  const resetImage = () => {
    setImageBase64(null);
    setImageMime(null);
    setImageName(null);
    setImageConsent(false);
  };

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t('showImport.title')}
      data-ai-show-modal
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onPaste={onPaste}
    >
      <div className="modal ai-import-modal">
        <div className="modal-header">
          <h3 className="modal-title">{t('showImport.title')}</h3>
          <button type="button" className="modal-close" aria-label={t('showImport.close')} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body ai-import-body">
          <p className="ai-import-hint">{t('showImport.hint')}</p>

          {!canWrite ? (
            <p className="ai-import-error" role="alert" data-ai-readonly>
              {t('showImport.readOnly')}
            </p>
          ) : (
            <>
              <div
                className={`ai-import-dropzone${dragOver ? ' is-over' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                data-ai-show-dropzone
              >
                {pdfName ? (
                  <span data-ai-show-filename>PDF · {pdfName}</span>
                ) : imageName ? (
                  <span data-ai-show-image-name>🖼 {imageName}</span>
                ) : (
                  <span>
                    {t('showImport.dropHint')}{' '}
                    <button type="button" className="btn btn-small" onClick={() => pdfInputRef.current?.click()}>
                      {t('showImport.choosePdf')}
                    </button>
                    {' · '}
                    <button type="button" className="btn btn-small" onClick={() => imageInputRef.current?.click()}>
                      {t('showImport.chooseImage')}
                    </button>
                  </span>
                )}
                <input
                  ref={pdfInputRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => void onFile(e.target.files?.[0])}
                />
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  style={{ display: 'none' }}
                  onChange={(e) => void onFile(e.target.files?.[0])}
                />
              </div>

              <textarea
                className="form-input"
                rows={5}
                value={text}
                placeholder={t('showImport.placeholder')}
                disabled={extracting || !!pdfBase64 || !!imageBase64}
                onChange={(e) => setText(e.target.value)}
                data-ai-show-text
              />

              {imageBase64 ? (
                <div className="ai-import-consent" data-ai-show-image-consent>
                  {/* Explicit, always-visible line: the image LEAVES the server
                      for the AI provider. No request is sent without the tick. */}
                  <p>{t('showImport.imageConsent')}</p>
                  <label>
                    <input
                      type="checkbox"
                      checked={imageConsent}
                      disabled={!canWrite || extracting}
                      onChange={(e) => setImageConsent(e.target.checked)}
                      data-ai-show-consent-check
                    />
                    {' '}{t('showImport.imageConsentAccept')}
                  </label>
                  <button type="button" className="btn btn-small" onClick={resetImage} data-ai-show-image-remove>
                    {t('showImport.removeImage')}
                  </button>
                </div>
              ) : null}

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!canExtract}
                  onClick={() => void runExtract()}
                  data-ai-show-extract
                >
                  {extracting ? t('showImport.extracting') : t('showImport.extract')}
                </button>
                {imageBase64 && !imageConsent ? (
                  <span className="text-dim" data-ai-show-consent-required>{t('showImport.consentRequired')}</span>
                ) : null}
              </div>
            </>
          )}

          {error ? (
            <p className="ai-import-error" role="alert" data-ai-show-error>{error}</p>
          ) : null}

          {warnings.length > 0 ? (
            <ul className="ai-import-warnings" data-ai-show-warnings>
              {warnings.slice(0, 6).map((w, i) => <li key={`${i}-${w}`}>{w}</li>)}
            </ul>
          ) : null}

          {proposed && entries.length === 0 && !extracting ? (
            <p className="ai-import-hint" role="status" data-ai-show-empty>{t('showImport.noFields')}</p>
          ) : null}

          {proposed && safeEntries.length > 0 ? (
            <ul className="ai-import-legs" data-ai-show-fields>
              {safeEntries.map((entry) => (
                <li key={entry.field} className="ai-import-leg" data-ai-show-field data-field={entry.field}>
                  <label className="ai-import-leg-head">
                    <input
                      type="checkbox"
                      checked={checked.has(entry.field)}
                      disabled={!canWrite || applying}
                      onChange={() => toggleField(entry.field)}
                      data-ai-show-check
                    />
                    <strong>{t(`showImport.fields.${entry.field}`, { defaultValue: entry.field })}</strong>
                    <span className="text-dim" data-ai-show-current>
                      {t('showImport.current')}: {formatFieldValue(current[entry.field])}
                    </span>
                    <span>→ {formatFieldValue(entry.value)}</span>
                    {isReplaceWarning(current[entry.field], entry.value) ? (
                      <span className="ai-import-duplicate" data-ai-show-replace>
                        {t('showImport.willBeReplaced')}
                      </span>
                    ) : null}
                  </label>
                </li>
              ))}
            </ul>
          ) : null}

          {proposed && moneyEntries.length > 0 ? (
            <div className="ai-import-money-group" data-ai-show-money-group>
              <h4 className="ai-import-money-title">{t('showImport.moneyGroup')}</h4>
              <p className="ai-import-hint">{t('showImport.moneyHint')}</p>
              <ul className="ai-import-legs">
                {moneyEntries.map((entry) => (
                  <li key={entry.field} className="ai-import-leg" data-ai-show-field data-field={entry.field} data-money="1">
                    <label className="ai-import-leg-head">
                      <input
                        type="checkbox"
                        checked={checked.has(entry.field)}
                        disabled={!canWrite || applying}
                        onChange={() => toggleField(entry.field)}
                        data-ai-show-check
                      />
                      <strong>{t(`showImport.fields.${entry.field}`, { defaultValue: entry.field })}</strong>
                      <span className="text-dim" data-ai-show-current>
                        {t('showImport.current')}: {formatFieldValue(current[entry.field])}
                      </span>
                      <span>→ {formatFieldValue(entry.value)}</span>
                      {isReplaceWarning(current[entry.field], entry.value) ? (
                        <span className="ai-import-duplicate" data-ai-show-replace>
                          {t('showImport.willBeReplaced')}
                        </span>
                      ) : null}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {venueCandidates.length > 0 ? (
            <div className="ai-import-venue" data-ai-show-venue>
              <h4 className="ai-import-money-title">{t('showImport.venueGroup')}</h4>
              <p className="ai-import-hint">
                {t('showImport.venueHint', { query: venueQuery || '—' })}
              </p>
              <ul>
                <li>
                  <label>
                    <input
                      type="radio"
                      name="ai-show-venue"
                      checked={venueChoice === ''}
                      disabled={!canWrite || applying}
                      onChange={() => setVenueChoice('')}
                      data-ai-show-venue-none
                    />
                    {' '}{t('showImport.venueDoNotTouch')}
                  </label>
                </li>
                {venueCandidates.map((c) => (
                  <li key={String(c.id)}>
                    <label>
                      <input
                        type="radio"
                        name="ai-show-venue"
                        checked={venueChoice === String(c.id)}
                        disabled={!canWrite || applying}
                        onChange={() => setVenueChoice(String(c.id))}
                        data-ai-show-venue-candidate={String(c.id)}
                      />
                      {' '}{c.name}{c.city ? ` — ${c.city}` : ''}{c.country ? ` (${c.country})` : ''}
                    </label>
                  </li>
                ))}
              </ul>
              <p className="ai-import-hint">{t('showImport.venueNoCreate')}</p>
            </div>
          ) : null}

          {unreadable.length > 0 ? (
            <div className="ai-import-unreadable" data-ai-show-unreadable>
              <strong>{t('showImport.notRead')}</strong>
              <p>{unreadable.join(', ')}</p>
            </div>
          ) : null}

          {proposed && entries.length > 0 ? (
            <p className="ai-import-hint" role="status" data-ai-show-not-selected>
              {checked.size === 0 ? t('showImport.nothingSelected') : null}
            </p>
          ) : null}

          {outcome ? (
            <p className="ai-import-hint" role="status" data-ai-show-outcome>
              {t('showImport.applied', { count: outcome.count })}
            </p>
          ) : null}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('showImport.close')}
          </button>
          {canWrite ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={!canApplyNow}
              onClick={() => void apply()}
              data-ai-show-apply
            >
              {applying ? t('showImport.applying') : t('showImport.apply', { count: checked.size })}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
